import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../ui/select';
import { RefreshCw, KeyRound, CheckCircle2, XCircle, AlertTriangle, Lock, Wand2 } from 'lucide-react';
import { SECURITY_LEVELS, DEFAULT_MAX_ATTEMPTS, DEFAULT_LOCKOUT_MS } from '../../../lib/securityAccess/constants';
import { ALGORITHMS, ALGORITHM_BY_ID, ALGORITHM_IDS, validateCustomFormula } from '../../../lib/securityAccess/algorithms';
import { parseHex32, toHex32String } from '../../../lib/securityAccess/hexUtils';
import { PHASE, STATUS, createInitialState, requestSeed, sendKey, tickLockout, resetSession } from '../../../lib/securityAccess/sessionEngine';
import AlgorithmVisualizer from './AlgorithmVisualizer';
import MessageTracePanel from './MessageTracePanel';

const STATUS_META = {
  [STATUS.NONE]: null,
  [STATUS.GRANTED]: { icon: CheckCircle2, label: 'Access Granted', className: 'text-green-600 border-green-600/40 bg-green-600/10' },
  [STATUS.INVALID_KEY]: { icon: XCircle, label: 'Invalid Key', className: 'text-destructive border-destructive/40 bg-destructive/10' },
  [STATUS.INVALID_SEQUENCE]: { icon: AlertTriangle, label: 'Invalid Sequence', className: 'text-amber-600 border-amber-600/40 bg-amber-600/10' },
  [STATUS.LOCKED_OUT]: { icon: Lock, label: 'Locked Out', className: 'text-destructive border-destructive/40 bg-destructive/10' },
};

const SecurityAccessSimulator = () => {
  const [level, setLevel] = useState(SECURITY_LEVELS[0]);
  const [algorithmId, setAlgorithmId] = useState(ALGORITHM_IDS.XOR);
  const [paramHex, setParamHex] = useState('0xA5A5A5A5');
  const [rotation, setRotation] = useState(8);
  const [customFormula, setCustomFormula] = useState('seed ^ param');
  const [maxAttempts, setMaxAttempts] = useState(DEFAULT_MAX_ATTEMPTS);
  const [forceZeroSeed, setForceZeroSeed] = useState(false);
  const [enteredKeyHex, setEnteredKeyHex] = useState('');
  const [session, setSession] = useState(() => createInitialState({ maxAttempts: DEFAULT_MAX_ATTEMPTS, lockoutDurationMs: DEFAULT_LOCKOUT_MS }));
  const [now, setNow] = useState(Date.now());

  const algorithm = ALGORITHM_BY_ID[algorithmId];
  const param = parseHex32(paramHex);
  const formulaCheck = algorithm.isCustom ? validateCustomFormula(customFormula) : { valid: true, error: null };
  const algoOpts = { rotation: Number(rotation) || 0, customFormula };
  const configValid = param !== null && (!algorithm.isCustom || formulaCheck.valid);

  const prevConfigRef = useRef({ algorithmId, param, rotation, customFormula });

  // Lockout countdown ticker.
  useEffect(() => {
    if (session.phase !== PHASE.LOCKED) return undefined;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [session.phase]);

  useEffect(() => {
    setSession((s) => tickLockout(s, now));
  }, [now]);

  // The expected key is a function of algorithm/param/rotation/formula at the moment a
  // seed was issued — if any of those change while a handshake is in progress, that
  // expected key is now stale, so start the handshake over rather than silently comparing
  // against an out-of-date value.
  useEffect(() => {
    const prev = prevConfigRef.current;
    const changed = prev.algorithmId !== algorithmId || prev.param !== param || prev.rotation !== rotation || prev.customFormula !== customFormula;
    prevConfigRef.current = { algorithmId, param, rotation, customFormula };
    if (changed && session.phase !== PHASE.IDLE) {
      setSession((s) => resetSession(s));
      setEnteredKeyHex('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [algorithmId, param, rotation, customFormula]);

  const handleLevelChange = (id) => {
    setLevel(SECURITY_LEVELS.find((l) => l.id === Number(id)));
    setSession((s) => resetSession(s));
    setEnteredKeyHex('');
  };

  const handleMaxAttemptsChange = (value) => {
    const n = Math.max(1, Number(value) || 1);
    setMaxAttempts(n);
    setSession((s) => ({ ...s, maxAttempts: n }));
  };

  const handleRequestSeed = () => {
    if (!configValid || session.phase === PHASE.LOCKED) return;
    const seedValue = forceZeroSeed ? 0 : Math.floor(Math.random() * 0x100000000);
    setSession((s) => requestSeed(s, { level, algorithmId, param, algoOpts, seedValue }));
    setEnteredKeyHex('');
  };

  const handleAutoFill = () => {
    if (session.expectedKey === null) return;
    setEnteredKeyHex(toHex32String(session.expectedKey));
  };

  const handleSendKey = () => {
    const enteredKey = parseHex32(enteredKeyHex);
    if (enteredKey === null) return;
    setSession((s) => sendKey(s, { level, enteredKey }));
  };

  const handleReset = () => {
    setSession((s) => resetSession(s));
    setEnteredKeyHex('');
    setForceZeroSeed(false);
  };

  const statusMeta = STATUS_META[session.status];
  const lockoutSecondsLeft = session.phase === PHASE.LOCKED && session.lockoutUntil ? Math.max(0, Math.ceil((session.lockoutUntil - now) / 1000)) : 0;
  const enteredKeyValid = parseHex32(enteredKeyHex) !== null;

  return (
    <div className="space-y-6" data-testid="security-access-simulator">
      <Card className="technical-card">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="font-chivo uppercase tracking-tight text-base">Session Configuration</CardTitle>
              <CardDescription>Fictional training algorithms only — see the disclaimer above.</CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="font-mono" data-testid="sa-phase-badge">
                {session.phase === PHASE.LOCKED
                  ? `Locked Out (${lockoutSecondsLeft}s)`
                  : session.phase === PHASE.GRANTED
                    ? 'Access Granted'
                    : session.phase === PHASE.SEED_REQUESTED
                      ? 'Seed Requested'
                      : 'Idle'}
              </Badge>
              <Button variant="outline" size="sm" onClick={handleReset} data-testid="sa-reset-button">
                <RefreshCw className="h-3.5 w-3.5" /> Reset
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Security Level</Label>
              <Select value={String(level.id)} onValueChange={handleLevelChange}>
                <SelectTrigger data-testid="sa-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SECURITY_LEVELS.map((l) => (
                    <SelectItem key={l.id} value={String(l.id)}>
                      {l.label} (0x{l.requestSubFn.toString(16).padStart(2, '0').toUpperCase()} / 0x{l.sendKeySubFn.toString(16).padStart(2, '0').toUpperCase()})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Training Algorithm</Label>
              <Select value={algorithmId} onValueChange={setAlgorithmId}>
                <SelectTrigger data-testid="sa-algorithm-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ALGORITHMS.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] font-mono text-muted-foreground">{algorithm.formula}</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Secret / Algorithm Parameter (hex)</Label>
              <Input
                className="font-mono"
                value={paramHex}
                onChange={(e) => setParamHex(e.target.value)}
                placeholder="0xA5A5A5A5"
                data-testid="sa-param-input"
              />
              {param === null && <p className="text-[11px] text-destructive">Enter a valid 1-8 digit hex value.</p>}
            </div>
            {algorithm.hasRotation && (
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Rotation Amount (n, 0-31)</Label>
                <Input
                  type="number"
                  min={0}
                  max={31}
                  className="font-mono"
                  value={rotation}
                  onChange={(e) => setRotation(e.target.value)}
                  data-testid="sa-rotation-input"
                />
              </div>
            )}
            {algorithm.isCustom && (
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Custom Formula</Label>
                <Input
                  className="font-mono"
                  value={customFormula}
                  onChange={(e) => setCustomFormula(e.target.value)}
                  placeholder="(seed ^ param) + 1"
                  data-testid="sa-custom-formula-input"
                />
                {!formulaCheck.valid && <p className="text-[11px] text-destructive">{formulaCheck.error}</p>}
                <p className="text-[11px] text-muted-foreground">Supported: + - ^ &amp; | &lt;&lt; &gt;&gt; ~, identifiers `seed`/`param`, hex/decimal literals, parentheses.</p>
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Lockout After (consecutive invalid keys)</Label>
              <Input
                type="number"
                min={1}
                className="font-mono"
                value={maxAttempts}
                onChange={(e) => handleMaxAttemptsChange(e.target.value)}
                data-testid="sa-max-attempts-input"
              />
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={forceZeroSeed} onChange={(e) => setForceZeroSeed(e.target.checked)} data-testid="sa-force-zero-checkbox" />
              Force seed 0x00000000 (demonstrate "already unlocked")
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-structure">
            <Button
              onClick={handleRequestSeed}
              disabled={!configValid || session.phase === PHASE.LOCKED}
              className="btn-premium"
              data-testid="sa-request-seed-button"
            >
              <KeyRound className="h-4 w-4" /> Request Seed
            </Button>

            <Input
              className="font-mono w-48"
              placeholder="Enter key (hex)"
              value={enteredKeyHex}
              onChange={(e) => setEnteredKeyHex(e.target.value)}
              aria-label="Enter key"
              data-testid="sa-key-input"
            />
            <Button
              variant="outline"
              onClick={handleAutoFill}
              disabled={session.phase !== PHASE.SEED_REQUESTED}
              data-testid="sa-autofill-button"
            >
              <Wand2 className="h-4 w-4" /> Auto-fill Expected Key
            </Button>
            <Button
              onClick={handleSendKey}
              disabled={!enteredKeyValid}
              data-testid="sa-send-key-button"
            >
              Send Key
            </Button>

            {statusMeta && (
              <Badge variant="outline" className={`font-mono flex items-center gap-1.5 ${statusMeta.className}`} data-testid="sa-status-badge">
                <statusMeta.icon className="h-3.5 w-3.5" /> {statusMeta.label}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <AlgorithmVisualizer seed={session.seed} algorithmId={algorithmId} param={param ?? 0} algoOpts={algoOpts} />
        <MessageTracePanel log={session.log} />
      </div>
    </div>
  );
};

export default SecurityAccessSimulator;
